"use client"

import { TableCell } from "@/components/ui/table"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Checkbox } from "@/components/ui/checkbox"

interface TableCellRendererProps {
  row: any
  fieldKey: string
  rowIndex: number
  editable: boolean
  fieldType: { type: string; options?: string[] }
  onChange: (rowIndex: number, key: string, value: any) => void
  width?: number
}

export function TableCellRenderer({
  row,
  fieldKey,
  rowIndex,
  editable,
  fieldType,
  onChange,
  width = 200,
}: TableCellRendererProps) {
  const cellStyle = { width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px`, overflowX: "hidden" }

  if (!editable) {
    const value = row[fieldKey]
    if (value === null || value === undefined) return <TableCell style={cellStyle}>-</TableCell>
    if (typeof value === "boolean") return <TableCell style={cellStyle}>{value ? "Yes" : "No"}</TableCell>
    if (typeof value === "object") return <TableCell style={cellStyle}>{JSON.stringify(value)}</TableCell>
    return <TableCell style={cellStyle} className="text-ellipsis">{String(value)}</TableCell>
  }

  const value = row[fieldKey] !== undefined ? String(row[fieldKey]) : ""

  switch (fieldType.type) {
    case "checkbox":
      return (
        <TableCell style={cellStyle}>
          <Checkbox
            checked={row[fieldKey] === true || row[fieldKey] === "true"}
            onCheckedChange={(checked) => onChange(rowIndex, fieldKey, checked)}
          />
        </TableCell>
      )
    case "select":
      return (
        <TableCell style={cellStyle}>
          <Select value={value} onValueChange={(val) => onChange(rowIndex, fieldKey, val)}>
            <SelectTrigger className="h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {fieldType.options?.map((option: string) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </TableCell>
      )
    case "textarea":
      return (
        <TableCell style={cellStyle}>
          <Textarea
            value={value}
            onChange={(e) => onChange(rowIndex, fieldKey, e.target.value)}
            className="min-h-[60px] resize-none"
          />
        </TableCell>
      )
    default:
      return (
        <TableCell style={cellStyle}>
          <Input
            type={fieldType.type}
            value={value}
            onChange={(e) => {
              const newValue = fieldType.type === "number" ? Number(e.target.value) : e.target.value
              onChange(rowIndex, fieldKey, newValue)
            }}
            className="h-8 text-ellipsis"
          />
        </TableCell>
      )
  }
}

